
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  HStack,
  Button,
  Text,
  Box,
  Select,
  FormControl,
  //FormLabel,
  Tooltip,
  IconButton,
  //useDisclosure
} from "@chakra-ui/react";
import { FiPlus, FiEdit } from 'react-icons/fi';
import { useState } from 'react';

const PointsExpirationModal = ({ isOpen, onClose }) => {
  const [expirationEnabled, setExpirationEnabled] = useState(true);
  const [expirationMonths, setExpirationMonths] = useState(12);
  const [exceptions, setExceptions] = useState([
    { type: "productCategory", value: "Electronics", action: "never" },
    { type: "customerTier", value: "Gold+", action: "extend", months: 24 }
  ]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [includeSuggestions, setIncludeSuggestions] = useState(false);

  const addException = () => {
    setExceptions([...exceptions, { type: "", value: "", action: "" }]);
  };

  const updateException = (index, field, value) => {
    const updated = [...exceptions];
    updated[index][field] = value;
    setExceptions(updated);
  };

  const removeException = (index) => {
    setExceptions(exceptions.filter((_, i) => i !== index));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Points Expiration Rules</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Default Settings */}
            <Box borderWidth="1px" borderRadius="lg" p={4}>
              <FormControl>
                <HStack>
                  <Checkbox
                    isChecked={expirationEnabled}
                    onChange={(e) => setExpirationEnabled(e.target.checked)}
                  >
                    Points expire after
                  </Checkbox>
                  <NumberInput
                    min={1}
                    max={36}
                    value={expirationMonths}
                    onChange={(val) => setExpirationMonths(val)}
                    isDisabled={!expirationEnabled}
                    width="100px"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text>months</Text>
                </HStack>
                <Checkbox
                  mt={2}
                  isChecked={!expirationEnabled}
                  onChange={(e) => setExpirationEnabled(!e.target.checked)}
                >
                  No expiration
                </Checkbox>
              </FormControl>
            </Box>

            {/* Exceptions */}
            <Box borderWidth="1px" borderRadius="lg" p={4}>
              <Text fontWeight="bold" mb={2}>
                Exceptions
              </Text>
              {exceptions.map((exception, index) => (
                <HStack key={index} mb={2}>
                  <Select
                    value={exception.type}
                    onChange={(e) => updateException(index, "type", e.target.value)}
                    width="180px"
                  >
                    <option value="">Select type</option>
                    <option value="productCategory">Product Category</option>
                    <option value="customerTier">Customer Tier</option>
                  </Select>
                  
                  {exception.type === "productCategory" && (
                    <Select
                      value={exception.value}
                      onChange={(e) => updateException(index, "value", e.target.value)}
                      width="180px"
                    >
                      <option value="Electronics">Electronics</option>
                      <option value="Apparel">Apparel</option>
                      <option value="Home Goods">Home Goods</option>
                    </Select>
                  )}
                  
                  {exception.type === "customerTier" && (
                    <Select
                      value={exception.value}
                      onChange={(e) => updateException(index, "value", e.target.value)}
                      width="180px"
                    >
                      <option value="Gold+">Gold+</option>
                      <option value="Platinum">Platinum</option>
                      <option value="VIP">VIP</option>
                    </Select>
                  )}
                  
                  <Select
                    value={exception.action}
                    onChange={(e) => updateException(index, "action", e.target.value)}
                    width="150px"
                  >
                    <option value="never">Never Expire</option>
                    <option value="extend">Extend Period</option>
                  </Select>
                  
                  {exception.action === "extend" && (
                    <>
                      <NumberInput
                        min={1}
                        max={36}
                        value={exception.months || 12}
                        onChange={(val) => updateException(index, "months", val)}
                        width="100px"
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text>months</Text>
                    </>
                  )}
                  
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeException(index)}
                  >
                    Remove
                  </Button>
                </HStack>
              ))}
              
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                onClick={addException}
                mt={2}
              >
                Add Exception
              </Button>
            </Box>

            {/* Notification Schedule */}
            <Box borderWidth="1px" borderRadius="lg" p={4}>
              <FormControl>
                <Checkbox
                  isChecked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                >
                  Send warnings
                </Checkbox>
                
                {notificationsEnabled && (
                  <VStack align="start" mt={3} pl={6}>
                    <HStack>
                      <Text>• 30 days before</Text>
                      <Tooltip label="Preview default template" placement="top">
                        <IconButton
                          icon={<FiEdit />}
                          aria-label="Edit template"
                          size="sm"
                          variant="ghost"
                        />
                      </Tooltip>
                    </HStack>
                    <HStack>
                      <Text>• 7 days before</Text>
                      <Tooltip label="Preview custom template" placement="top">
                        <IconButton
                          icon={<FiEdit />}
                          aria-label="Edit template"
                          size="sm"
                          variant="ghost"
                        />
                      </Tooltip>
                    </HStack>
                  </VStack>
                )}
                
                <Checkbox
                  mt={3}
                  isChecked={includeSuggestions}
                  onChange={(e) => setIncludeSuggestions(e.target.checked)}
                  isDisabled={!notificationsEnabled}
                >
                  Include redemption suggestions
                </Checkbox>
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={() => { /* Save logic */ }}>
            Save Draft
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PointsExpirationModal;